import type { DragEvent, FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconDelete, IconPlus } from '~/ui/core/icons';
import { Input } from '~/ui/core/input/Input';

import type { ProfileTaskWorkflowStep } from '../model/profile-task';
import { profileTaskTranslationKeys } from '../profile-task-translations';
import { ProfileTaskRichTextField } from './ProfileTaskRichTextField';
import { WorkflowReorder } from './WorkflowReorder';

export type TaskWorkflowStepIssueField = 'workflow.description' | 'workflow.title';

const moveItem = <T,>(items: readonly T[], fromIndex: number, toIndex: number): readonly T[] => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) {
        return items;
    }
    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    if (moved !== undefined) {
        next.splice(toIndex, 0, moved);
    }
    return next;
};

/** Edits a task's ordered workflow steps: add, remove, reorder, and each step's title and description. */
export const TaskWorkflowStepsEditor: FunctionComponent<{
    readonly hasIssue?: (field: TaskWorkflowStepIssueField, workflowStepId: string) => boolean;
    /** Keeps the remove action off the last steps, e.g. where a workflow cannot be cleared. */
    readonly minSteps?: number;
    readonly onChange: (steps: readonly ProfileTaskWorkflowStep[]) => void;
    readonly steps: readonly ProfileTaskWorkflowStep[];
    readonly taskId: string;
}> = ({ hasIssue, minSteps = 0, onChange, steps, taskId }) => {
    const translate = useTranslate();
    const moveStep = (fromIndex: number, toIndex: number): void => {
        onChange(moveItem(steps, fromIndex, toIndex));
    };
    const handleDrop = (event: DragEvent<HTMLElement>, toIndex: number): void => {
        event.preventDefault();
        const fromIndex = Number.parseInt(event.dataTransfer.getData('application/x-profile-task-workflow'), 10);
        if (Number.isInteger(fromIndex)) {
            moveStep(fromIndex, toIndex);
        }
    };
    const updateStep = (workflowStepId: string, change: Partial<ProfileTaskWorkflowStep>): void => {
        onChange(steps.map((step) => (step.workflowStepId === workflowStepId ? { ...step, ...change } : step)));
    };

    return (
        <section className="flex flex-col gap-m" data-testid="task-workflow-editor">
            <div className="flex items-center justify-between gap-m">
                <h3 className="m-0 text-body-xs font-medium text-text-primary">
                    {translate(profileTaskTranslationKeys.workflow)}
                </h3>
                <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                        onChange([
                            ...steps,
                            { description: '', title: '', workflowStepId: globalThis.crypto.randomUUID() },
                        ])
                    }
                >
                    <IconPlus />
                    {translate(profileTaskTranslationKeys.addWorkflowStep)}
                </Button>
            </div>
            <ol className="m-0 flex list-none flex-col gap-m p-0">
                {steps.map((step, index) => (
                    <li
                        key={step.workflowStepId}
                        className="flex list-none flex-col gap-xl rounded-xl bg-surface-subtle p-l"
                        draggable={true}
                        onDragOver={(event) => event.preventDefault()}
                        onDragStart={(event) => {
                            event.dataTransfer.effectAllowed = 'move';
                            event.dataTransfer.setData('application/x-profile-task-workflow', index.toString());
                        }}
                        onDrop={(event) => handleDrop(event, index)}
                    >
                        <div className="flex flex-col text-body-s font-medium leading-normal text-text-primary">
                            <div className="mb-s flex items-center justify-between gap-m">
                                <label htmlFor={`profile-task-workflow-title-${step.workflowStepId}`}>
                                    {translate(profileTaskTranslationKeys.workflowStepTitle)}
                                </label>
                                <div className="flex items-center gap-s">
                                    <WorkflowReorder
                                        index={index}
                                        total={steps.length}
                                        onMove={(nextIndex) => moveStep(index, nextIndex)}
                                    />
                                    {steps.length > minSteps ? (
                                        <Button
                                            aria-label={translate(profileTaskTranslationKeys.removeWorkflowStep)}
                                            iconOnly={true}
                                            size="sm"
                                            variant="tertiary"
                                            onClick={() =>
                                                onChange(
                                                    steps.filter(
                                                        ({ workflowStepId }) => workflowStepId !== step.workflowStepId,
                                                    ),
                                                )
                                            }
                                        >
                                            <IconDelete />
                                        </Button>
                                    ) : null}
                                </div>
                            </div>
                            <Input
                                data-testid="profile-task-workflow-title-input"
                                id={`profile-task-workflow-title-${step.workflowStepId}`}
                                value={step.title}
                                onChange={(event) => updateStep(step.workflowStepId, { title: event.target.value })}
                            />
                            {hasIssue?.('workflow.title', step.workflowStepId) === true ? (
                                <span className="mt-s text-body-xs font-normal text-text-error">
                                    {translate(profileTaskTranslationKeys.validationWorkflowTitle)}
                                </span>
                            ) : null}
                        </div>
                        <ProfileTaskRichTextField
                            compact={true}
                            field={`workflow.${step.workflowStepId}.description`}
                            label={translate(profileTaskTranslationKeys.workflowStepDescription)}
                            taskId={taskId}
                            value={step.description}
                            onChange={(description) => updateStep(step.workflowStepId, { description })}
                        />
                        {hasIssue?.('workflow.description', step.workflowStepId) === true ? (
                            <span className="text-body-xs font-normal text-text-error">
                                {translate(profileTaskTranslationKeys.validationWorkflowDescription)}
                            </span>
                        ) : null}
                    </li>
                ))}
            </ol>
        </section>
    );
};
